import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { PaperIconComponent, PaperButtonComponent, PaperButtonIntent } from '@ricoh-paper/angular';
import { LocaleService } from '../../../../core/services/locale.service';
import { NewsItem } from '../../../../core/models/home-dashboard.model';

/**
 * Persistent banner for unread critical articles.
 * Shows at the top of the home page with a red accent stripe.
 * Clicking "Read now" opens the article; clicking dismiss marks as read.
 */
@Component({
  selector: 'im-critical-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperIconComponent, PaperButtonComponent, TranslocoPipe],
  template: `
    @for (item of items(); track item._id) {
      <div class="critical-banner" [class.banner--urgent]="item.urgency === 'urgent'">
        <div class="banner-accent"></div>
        <div class="banner-content">
          <div class="banner-icon">
            <paper-icon name="alert-circle" [size]="16" />
          </div>
          <div class="banner-text">
            <span class="banner-label">{{ urgencyLabel(item) }}</span>
            <span class="banner-title">{{ item.title }}</span>
          </div>
          <paper-button [intent]="bannerIntent(item)" size="sm" (click)="read.emit(item)">{{ 'home.components.critical_banner.read_now' | transloco }}</paper-button>
          <paper-button class="banner-dismiss" intent="ghost" size="xs" shape="icon" icon="x" [iconSize]="12" (click)="dismiss.emit(item)" [ariaLabel]="'home.components.critical_banner.dismiss' | transloco" [title]="'home.components.critical_banner.mark_as_read' | transloco" />
        </div>
      </div>
    }
  `,
  styles: `
    /* Critical banner — danger semantic. Uses --danger as the accent
       and color-mix to derive the soft tints. The urgent variant
       overrides the same custom property for warning. */
    .critical-banner {
      --banner-accent: var(--danger);
      --banner-text-on-tint: var(--error-text);
      position: relative;
      margin-bottom: 12px;
      border-radius: var(--radius-sm, 8px);
      overflow: hidden;
      background: color-mix(in srgb, var(--banner-accent) 6%, transparent);
      border: 1px solid color-mix(in srgb, var(--banner-accent) 15%, transparent);
      animation: bannerIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) both;
    }

    @keyframes bannerIn {
      from { opacity: 0; transform: translateY(-8px); }
      to { opacity: 1; transform: translateY(0); }
    }

    .banner-accent {
      position: absolute;
      top: 0;
      left: 0;
      width: 3px;
      height: 100%;
      background: var(--banner-accent);
    }

    .banner-content {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 14px 10px 18px;
    }

    .banner-icon {
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background: color-mix(in srgb, var(--banner-accent) 12%, transparent);
      color: var(--banner-text-on-tint);
      flex-shrink: 0;
    }

    .banner-text {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 1px;
    }

    .banner-label {
      font-size: 9px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--banner-text-on-tint);
    }

    .banner-title {
      font-size: 13px;
      font-weight: 600;
      color: var(--text);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .banner-dismiss {
      flex-shrink: 0;
      --im-button-icon-dimension: 24px;
      --im-button-min-height: 24px;
      --im-button-icon-radius: 50%;
      --im-button-background: transparent;
      --surface-hover: transparent;
      --im-button-text: var(--text-tertiary);
      --im-button-text-hover: var(--text);
    }

    /* Urgent variant — amber instead of red */
    .banner--urgent {
      --banner-accent: var(--warning);
      --banner-text-on-tint: var(--warning-text);
    }

    @media (prefers-reduced-motion: reduce) {
      .critical-banner { animation: none; }
    }
  `,
})
export class CriticalBannerComponent {
  readonly items = input.required<NewsItem[]>();
  readonly read = output<NewsItem>();
  readonly dismiss = output<NewsItem>();
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  protected urgencyLabel(item: NewsItem): string {
    this.locale.locale();
    return this.transloco.translate(item.urgency === 'critical' ? 'home.components.critical_banner.critical' : 'home.components.critical_banner.urgent');
  }

  /** Map item urgency → paper-button intent.
   *  critical → destructive (red), urgent → warning (amber). */
  protected bannerIntent(item: NewsItem): PaperButtonIntent {
    return item.urgency === 'urgent' ? 'warning' : 'destructive';
  }
}
